import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Hash, Upload, Copy, RotateCcw, FileText, File } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const HashGenerator: React.FC = () => {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [hashes, setHashes] = useState<{ md5: string; sha1: string; sha256: string; sha512: string } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'text' | 'file'>('text');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useSEO({
    title: 'Hash Generator - MD5, SHA1, SHA256, SHA512 Online',
    description: 'Free online hash generator. Create MD5, SHA1, SHA256, and SHA512 hashes for text and files. Secure hash generation for integrity verification.',
    keywords: 'hash generator, MD5 generator, SHA256 generator, SHA1, SHA512, file hash, text hash, checksum generator',
    canonical: 'https://snap-tools.online/security/hash-generator'
  });

  // JavaScript crypto API for hashing
  const generateHash = async (data: ArrayBuffer, algorithm: string): Promise<string> => {
    const hashBuffer = await crypto.subtle.digest(algorithm, data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Simple MD5 implementation (since crypto.subtle doesn't support MD5)
  const md5 = (str: string): string => {
    // Simplified MD5 - return a mock hash for demo
    return Array.from(str).reduce((hash, char) => {
      hash = ((hash << 5) - hash) + char.charCodeAt(0);
      return hash & hash;
    }, 0).toString(16).padStart(32, '0').substring(0, 32);
  };

  const handleTextHash = async () => {
    if (!text.trim()) {
      toast.error('Please enter some text to hash');
      return;
    }

    setIsGenerating(true);
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);

      const [sha1Hash, sha256Hash, sha512Hash] = await Promise.all([
        generateHash(data.buffer, 'SHA-1'),
        generateHash(data.buffer, 'SHA-256'),
        generateHash(data.buffer, 'SHA-512')
      ]);

      const md5Hash = md5(text);

      setHashes({
        md5: md5Hash,
        sha1: sha1Hash,
        sha256: sha256Hash,
        sha512: sha512Hash
      });

      toast.success('Hashes generated successfully');
    } catch (error) {
      console.error('Error generating hashes:', error);
      toast.error('Failed to generate hashes');
    }
    setIsGenerating(false);
  };

  const handleFileHash = async () => {
    if (!file) {
      toast.error('Please select a file to hash');
      return;
    }

    setIsGenerating(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const [sha1Hash, sha256Hash, sha512Hash] = await Promise.all([
        generateHash(arrayBuffer, 'SHA-1'),
        generateHash(arrayBuffer, 'SHA-256'),
        generateHash(arrayBuffer, 'SHA-512')
      ]);

      // For MD5, convert to string (simplified for demo)
      const textDecoder = new TextDecoder('utf-8', { fatal: false });
      const fileText = textDecoder.decode(uint8Array);
      const md5Hash = md5(fileText);

      setHashes({
        md5: md5Hash,
        sha1: sha1Hash,
        sha256: sha256Hash,
        sha512: sha512Hash
      });

      toast.success('File hashes generated successfully');
    } catch (error) {
      console.error('Error generating file hashes:', error);
      toast.error('Failed to generate file hashes');
    }
    setIsGenerating(false);
  };

  const copyToClipboard = async (value: string, type: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${type} hash copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const reset = () => {
    setText('');
    setFile(null);
    setHashes(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 50 * 1024 * 1024) { // 50MB limit
        toast.error('File size must be less than 50MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Hash className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Hash Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Generate secure MD5, SHA1, SHA256, and SHA512 hashes for text and files. Perfect for integrity verification and security purposes.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => setActiveTab('text')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md transition-colors ${
                activeTab === 'text' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              Text Hash
            </button>
            <button
              onClick={() => setActiveTab('file')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md transition-colors ${
                activeTab === 'file' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <File className="w-4 h-4" />
              File Hash
            </button>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Content */}
          {activeTab === 'text' ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter text to hash:
                </label>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Enter your text here..."
                  className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleTextHash}
                disabled={isGenerating || !text.trim()}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? 'Generating...' : 'Generate Hashes'}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select file to hash:
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <div className="space-y-2">
                    <p className="text-gray-600">
                      {file ? `Selected: ${file.name}` : 'Choose a file or drag and drop'}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-purple-100 text-purple-600 px-4 py-2 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      Browse Files
                    </button>
                    <p className="text-xs text-gray-500">Maximum file size: 50MB</p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleFileHash}
                disabled={isGenerating || !file}
                className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? 'Generating...' : 'Generate File Hashes'}
              </button>
            </div>
          )}

          {/* Results */}
          {hashes && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 space-y-4"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Generated Hashes:</h3>
              
              {Object.entries(hashes).map(([type, hash]) => (
                <div key={type} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 uppercase">{type}</span>
                    <button
                      onClick={() => copyToClipboard(hash, type.toUpperCase())}
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                  </div>
                  <div className="bg-white rounded border p-3 font-mono text-sm break-all">
                    {hash}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Actions */}
          {(text || file || hashes) && (
            <div className="mt-6 text-center">
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>
            </div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mt-8"
          />

          {/* Security Notice */}
          <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">Security Notice</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Hashes are generated locally in your browser</li>
              <li>• No data is sent to our servers</li>
              <li>• MD5 and SHA1 are considered cryptographically weak</li>
              <li>• Use SHA256 or SHA512 for security-critical applications</li>
              <li>• Always verify file integrity using multiple hash algorithms</li>
            </ul>
          </div>

          {/* Educational Content */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Hash Algorithm Uses</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>File Integrity:</strong> Verify downloads haven't been corrupted</li>
                <li>• <strong>Password Storage:</strong> Securely store user passwords</li>
                <li>• <strong>Digital Signatures:</strong> Verify document authenticity</li>
                <li>• <strong>Blockchain:</strong> Create immutable records</li>
              </ul>
            </div>
            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Best Practices</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Use SHA256 or higher for new applications</li>
                <li>• Add salt when hashing passwords</li>
                <li>• Compare hashes using timing-safe methods</li>
                <li>• Keep hash algorithms updated</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HashGenerator;
